/**
 * PATH: packages/email-hub/src/feature/lib/inboxOrder.ts
 * FILE_ID: EMAILHUB-LIB-INBOXORDER-001
 * PURPOSE: Session-scoped record of the inbox's CURRENT row order so the
 *          full-page reader can step to the next/previous email (j / k)
 *          without refetching or guessing the sort.
 * WHY:     FEAT-2026-07-06-COMPOSER-MICRO §reader nav — Gmail's j/k flow.
 *          The reader renders a single email by id; only the inbox knows
 *          what "next" means (dedupe + sort + filters applied). The inbox
 *          publishes its rendered order here; the reader consumes it.
 * FLOW:
 *   InboxPage (deduped rows) ──recordInboxOrder──▶ memory + sessionStorage
 *   EmailReader (j/k)        ──siblingEmailIds──▶ { prevId, nextId }
 * SAFETY: ids only (no row content); sessionStorage is per-tab, cleared
 *         with the tab — same tenancy posture as useEmails.listCache.
 */

const STORAGE_KEY = 'qf:inbox-order:v1'

let memoryOrder: string[] | null = null

function readPersisted(): string[] {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

/** Publish the inbox's rendered order. Pure input: ordered email ids. */
export function recordInboxOrder(ids: string[]): void {
  memoryOrder = ids
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(0, 200)))
  } catch {
    // Quota/private-mode — memory copy still serves this page lifetime.
  }
}

/** The reader's neighbours in the last-seen inbox order.
 *  Pure: currentId → { prevId, nextId } (null at the ends / unknown id). */
export function siblingEmailIds(currentId: string): {
  prevId: string | null
  nextId: string | null
} {
  const order = memoryOrder ?? readPersisted()
  const idx = order.indexOf(currentId)
  if (idx < 0) return { prevId: null, nextId: null }
  return {
    prevId: idx > 0 ? order[idx - 1] : null,
    nextId: idx < order.length - 1 ? order[idx + 1] : null,
  }
}

/** Test/logout hook — forget the published order. */
export function clearInboxOrder(): void {
  memoryOrder = null
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
